import { useAuth } from './useAuth';

function resolveRequestUrl(input: RequestInfo | URL): URL {
  const raw = typeof input === 'string'
    ? input
    : input instanceof URL
      ? input.href
      : input.url;
  return new URL(raw, window.location.origin);
}

function isProtectedSameOriginUrl(url: URL): boolean {
  return url.origin === window.location.origin
    && (url.pathname.startsWith('/api/') || url.pathname.startsWith('/data/'));
}

function mergeHeaders(input: RequestInfo | URL, init?: RequestInit): Headers {
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => {
      headers.set(key, value);
    });
  }
  return headers;
}

/**
 * Drop-in replacement for `fetch()` that injects `Authorization: Bearer …`
 * when the user is authenticated. External requests (absolute URLs to other
 * origins) are passed through without modification.
 *
 * Can be called from anywhere — composable setup context is not required
 * because `useAuth()` is a singleton.
 */
export function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const { accessToken, authDisabled } = useAuth();
  const url = resolveRequestUrl(input);
  const headers = mergeHeaders(input, init);
  const protectedSameOrigin = isProtectedSameOriginUrl(url);

  if (authDisabled) {
    return fetch(input, init);
  }

  const token = accessToken.value;

  if (!token) {
    if (protectedSameOrigin && !headers.has('Authorization')) {
      return Promise.resolve(new Response('Unauthorized', { status: 401, statusText: 'Unauthorized' }));
    }
    return fetch(input, init);
  }

  if (protectedSameOrigin && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return fetch(input, { ...init, headers });
}